import { type Revision, type RevisionConflicts, RevisionPageType } from '../../types';
import { mutations } from '../../utils';
import { getPageDescendantsInRevision } from './getPageDescendantsInRevision';
import { getPageParentInRevision } from './getPageParentInRevision';

interface RemovePageInRevisionOptions {
    /**
     * When deleting the last page of a group, should it also remove the group ?
     * Defaults to false
     */
    deleteEmptyGroupParent?: boolean;
}

/**
 * Remove a page from a revision.
 * @param revision - The revision to update.
 * @param pageId - The ID of the page to remove.
 */
export function removePageInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    options: RemovePageInRevisionOptions = {}
): void {
    const page = mutations.getIn(revision, ['pages', pageId] as const);
    if (!page) {
        return;
    }

    const parent = getPageParentInRevision(revision, pageId);

    if (
        options.deleteEmptyGroupParent &&
        parent &&
        mutations.get(parent, 'type') === RevisionPageType.Group &&
        mutations.get(parent, 'pages').length === 1
    ) {
        removePageInRevision(revision, mutations.get(parent, 'id'));
        return;
    }

    // Compute pages to be removed
    const toDelete: string[] = [mutations.get(page, 'id')];
    const pageType = mutations.get(page, 'type');
    if (pageType === RevisionPageType.Document || pageType === RevisionPageType.Group) {
        const descendants = getPageDescendantsInRevision(revision, pageId);
        descendants.forEach((child) => {
            toDelete.push(mutations.get(child, 'id'));
        });
    }

    // Remove from its parent
    mutations.delArrayValue(
        parent ? mutations.get(parent, 'pages') : mutations.get(revision, 'rootPages'),
        pageId
    );

    toDelete.forEach((toDeletePageId) => {
        // Remove from the pages index
        mutations.del(mutations.get(revision, 'pages'), toDeletePageId);

        // Remove from conflicts if any
        const conflicts = mutations.get(revision, 'conflicts') as
            | mutations.MutableData<RevisionConflicts>
            | undefined;
        if (conflicts && mutations.getIn(conflicts, ['pages', toDeletePageId] as const)) {
            mutations.delIn(conflicts, ['pages', toDeletePageId] as const);
        }
    });
}
